import { DataTable } from "primereact/datatable";
import React, { useCallback, useLayoutEffect, useRef } from "react";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { locale, addLocale, FilterMatchMode } from "primereact/api";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { MultiSelect } from "primereact/multiselect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTableColumns, faFilterCircleXmark, faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { classNames } from "primereact/utils";
import { useState } from "react";
import { InputText } from 'primereact/inputtext';
import func, { getNestedValue } from "../../services/functions";
import { Calendar } from 'primereact/calendar';
import moment from "moment";
import "./prime_table.scss"
import { MyMultiSelect } from "./AddRemoveColumns";
import { defaultTableRows } from "../../services/data";
import TooltipOverlay from "../global/TooltipOverlay";
import useIsMobile from "../../hooks/useIsMobile";
import MobileCardList from "./MobileCardList/MobileCardList";
import { useAppContext } from "../../AppContext";

const mergeColumnsWithPreferences = (columns, savedColumns) => {
  const savedList = Array.isArray(savedColumns)
    ? savedColumns.filter((sCol) => sCol && typeof sCol.field === "string")
    : [];
  const savedFields = new Set(savedList.map((col) => col.field));
  const missingColumns = columns
    .filter((col) => !savedFields.has(col.field))
    .map((col) => ({
      field: col.field,
      show: !!col.default || !!col.persisted,
      ...(col?.persisted ? { persisted: true } : {}),
    }));

  return savedList
    .map((sCol) => {
      const column = columns.find((col) => col.field === sCol.field);
      return {
        field: sCol.field,
        show: column?.persisted ? true : !!sCol.show,
        ...(column?.persisted ? { persisted: true } : {}),
        ...(sCol.width ? { width: sCol.width } : {}),
      };
    })
    .concat(missingColumns);
};

const PrimeTable = ({
  filterDisplay = "row",
  dataKey,
  defaultFilters,
  columns = [],
  filters: [serverFilters, setServerFilters] = [],
  showGlobalSearch = false,
  searchableKeys = [],
  tableRows: [tableRowsOutside, setTableRowsOutside] = [],
  persistedRowPerPage:[persistedRowPerPage, setPersistedRowPerPage]=[],
  headerButtonsLeft,
  headerButtonsRight,
  headerIcons,
  selectedColumns: [selectedColumns, setSelectedColumns] = [],
  showFilters = false,
  responsiveLayout,
  children,
  value,
  customRowReorderHandle,
  onClearFilter,
  clearFilters = true,
  onRemoteDataChange,
  showSelectColumns=true,
  columnDeps=[],
  showHeader=true,
  likeValues=true,
  stripedRows=true,
  simpleTable=false,
  rowReorder=true,
  paginator=true,
  isColSort=true,
  defaultSortCriteria=null,
  compact=false,
  parseFilterValue=true,
  noCard=false,
  gridKey,
  ...props
}) => {


  const [filterRefresh,setFilterRefresh]=useState(null)
  const mobileFilterOpenRef = useRef(false);
  const { t, i18n } = useTranslation("translations");
  const { debounce, swalAlert } = func;
  const [tableRowsInside,setTableRowsInside]=useState({
    totalRows: value?.length ?? 0,
    rowsPerPage: defaultTableRows?.rowsPerPage,
    currentPage: 1,
  })
  const [globalFilterValue, setGlobalFilterValue] = useState("")
  const [filterRefreshed, setFilterRefreshed] = useState(null);
  const columnPanelRef = useRef(null);
  const { settings: [settings], saveGridPreference } = useAppContext() ?? { settings: [] };
  const gridPreferenceKey = gridKey;

  const colWidthStyleRef = useRef(null);
  const saveGridPreferenceTimeoutRef = useRef(null);
  const pendingGridPreferenceRef = useRef(null);
  const flushGridPreferenceSaveRef = useRef(null);
  const hydratedGridKeyRef = useRef(null);
  const hydratedColumnsRef = useRef(null);
  const hasLocalChangesRef = useRef(false);
  const wrapperRef = useRef(null);
  const tableIdRef = useRef('pt_' + Math.random().toString(36).slice(2, 9));

  const queueGridPreferenceSave = useCallback((nextColumns) => {
    if (!gridPreferenceKey || !saveGridPreference || !nextColumns) return;
    hasLocalChangesRef.current = true;
    const columnsToSave = nextColumns.map((col) => ({
      field: col.field,
      show: !!col.show,
      ...(col.width ? { width: col.width } : {}),
    }));
    pendingGridPreferenceRef.current = columnsToSave;
    if (saveGridPreferenceTimeoutRef.current) {
      clearTimeout(saveGridPreferenceTimeoutRef.current);
    }
    saveGridPreferenceTimeoutRef.current = setTimeout(() => {
      saveGridPreferenceTimeoutRef.current = null;
      pendingGridPreferenceRef.current = null;
      saveGridPreference(gridPreferenceKey, columnsToSave).catch(() => {});
    }, 500);
  }, [gridPreferenceKey, saveGridPreference]);

  const flushGridPreferenceSave = useCallback(() => {
    if (!saveGridPreferenceTimeoutRef.current) return;
    clearTimeout(saveGridPreferenceTimeoutRef.current);
    saveGridPreferenceTimeoutRef.current = null;
    const pendingColumns = pendingGridPreferenceRef.current;
    pendingGridPreferenceRef.current = null;
    if (pendingColumns && gridPreferenceKey && saveGridPreference) {
      saveGridPreference(gridPreferenceKey, pendingColumns).catch(() => {});
    }
  }, [gridPreferenceKey, saveGridPreference]);

  useEffect(() => {
    flushGridPreferenceSaveRef.current = flushGridPreferenceSave;
  });

  const setSelectedColumnsWithSave = useCallback((nextSelectedColumns) => {
    if (!setSelectedColumns) return;
    setSelectedColumns((prevSelectedColumns) => {
      const resolvedColumns = typeof nextSelectedColumns === "function"
        ? nextSelectedColumns(prevSelectedColumns)
        : nextSelectedColumns;
      queueGridPreferenceSave(resolvedColumns);
      return resolvedColumns;
    });
  }, [setSelectedColumns, queueGridPreferenceSave]);

  const handleColumnResizeEnd = useCallback((e) => {
    if (!setSelectedColumns) return;
    const field = e.column.props.field;
    const width = e.element.offsetWidth + 'px';
    setSelectedColumnsWithSave(prev =>
      prev.map(col => col.field === field ? { ...col, width } : col)
    );
  }, [setSelectedColumns, setSelectedColumnsWithSave]);

  useEffect(() => {
    return () => {
      flushGridPreferenceSaveRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!gridPreferenceKey || !setSelectedColumns) return;
    if (hydratedGridKeyRef.current !== gridPreferenceKey) {
      hydratedGridKeyRef.current = gridPreferenceKey;
      hydratedColumnsRef.current = null;
      hasLocalChangesRef.current = false;
    }
    if (hasLocalChangesRef.current) return;
    const savedColumns = settings?.gridPreferences?.[gridPreferenceKey];
    if (!Array.isArray(savedColumns)) return;
    if (hydratedColumnsRef.current === savedColumns) return;
    hydratedColumnsRef.current = savedColumns;
    setSelectedColumns(mergeColumnsWithPreferences(columns, savedColumns));
  }, [gridPreferenceKey, settings, setSelectedColumns, columns]);



  const tableRows = tableRowsOutside ? {...tableRowsOutside,rowsPerPage:persistedRowPerPage?? tableRowsOutside.rowsPerPage}:{ ...tableRowsInside, totalRows: value?.length ?? 0, }
  const setTableRows=setTableRowsOutside??setTableRowsInside
  const numberOfChildren = React.Children.count(children || []);

  const [clientFilters,setClientFilters]=useState({})
  const isMobile = useIsMobile();

  const filters=simpleTable? clientFilters:serverFilters
  const setFilters=simpleTable? setClientFilters: setServerFilters

  const defaultClientFilters=useCallback(()=>{
    const _mappedFilters={}
    columns.forEach(col=>{
      const key=col.field
      
      if(col.filter==true){
        if(col.filterType=="TRISTATECHECKBOX"){
          Object.assign(_mappedFilters,{[key]:{value:null,matchMode: FilterMatchMode.EQUALS}})
        }else if(col.filterType=="DROPDOWN"){
          Object.assign(_mappedFilters,{[key]:{value:null,matchMode: FilterMatchMode.EQUALS}})
        }else if(col.filterType=="MULTISELECT"){
          Object.assign(_mappedFilters,{[key]:{value:null,matchMode: FilterMatchMode.IN}})
        }else{
          Object.assign(_mappedFilters,{[key]:{value:null,matchMode: FilterMatchMode.CONTAINS}})
        }
      }

    })
    return _mappedFilters
  },[columns])

  useLayoutEffect(() => {
    if(simpleTable){
      const _mappedFilters=defaultClientFilters()
      setFilters(_mappedFilters)
    }
  }, [simpleTable,columns]);

  const onSort =(data)=>{
    // const newSortDirection = filters.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    let newTableRows=tableRows
    setTableRows(tableRows=>{
      newTableRows = {
        ...tableRows,
        sortCriteria:{field: data.sortField,direction:data.sortOrder === 1 ? 'ASC' : 'DESC' }
      };
      if (
        data.sortOrder == 1 &&
        tableRows?.sortCriteria?.direction == "DESC" &&
        tableRows?.sortCriteria?.field === data.sortField
      ) {
        if(defaultSortCriteria?.field ==tableRows?.sortCriteria?.field){
          newTableRows.sortCriteria = null;
        }else{
          newTableRows.sortCriteria = defaultSortCriteria;
        }
      }
      return newTableRows
    });
    if (onRemoteDataChange)
      onRemoteDataChange({ tableRows: newTableRows });
  }

  const totalRows =  tableRows.totalRows

  useEffect(() => {
    addLocale("alb", {
      startsWith: "Fillon me",
      contains: "Përmban",
      notContains: "Nuk Përmban",
      endsWith: "Përfundon me",
      equals: "E Barabartë",
      notEquals: "Jo të Barabartë",
      noFilter: "Pa Filtër",
    });
  }, []);

  useEffect(() => {
    locale(i18n.language);
  }, [i18n.language]);

  const clearFilter = useCallback(() => {
    if(simpleTable){
      setFilters(defaultClientFilters());
      setFilterRefreshed((refreshed)=>(refreshed==null || refreshed==false)? true:false)
    }else{
      setFilters({...filters,params:defaultFilters?.params});
      onRemoteDataChange({ filters: defaultFilters })
      setFilterRefreshed((refreshed)=>(refreshed==null || refreshed==false)? true:false)
    }
    setGlobalFilterValue("");
  }, [filters,filterRefreshed,onClearFilter,...columnDeps]);

  const newColumns=useMemo(()=>{
    if (!selectedColumns) return [];
    return columns.filter(col => selectedColumns?.every((sCol=>sCol.field!==col?.field))).map(foundCol=>({...foundCol,show:!!foundCol.persisted,newCol:true}))

  },[selectedColumns, i18n.language, filters, ...columnDeps])

  const currentColumns = useMemo(() => {
    if (!selectedColumns) return [];
    let currCols = []
    selectedColumns.forEach(sCol=>{
      const currCol=columns.find(col=>col.field===sCol.field)
      // Override minWidth to 50px on ALL columns so PrimeReact's gate check
      // (reads th.style.minWidth) allows shrinking. Remove maxWidth so it
      // doesn't fight the injected CSS. Actual widths come from <style> element.
      if(currCol)currCols.push({...currCol,show:sCol.show, style:{...currCol.style, minWidth:'50px', maxWidth:'none'}})
    })
    return currCols
  }, [selectedColumns, i18n.language, filters, ...columnDeps]);

  let columnsToDisplay=useMemo(()=>!selectedColumns?[]:currentColumns.filter(fCol=>fCol.show || fCol.persisted),[currentColumns])

  let tableColumns = selectedColumns ?  columnsToDisplay: columns;

  // Inject column widths via a <style> element in <head>, placed BEFORE
  // PrimeReact's own style elements so that resizeTableCells CSS (added
  // later in <head>) takes priority when the user resizes.
  // Runs synchronously in useLayoutEffect to avoid flash of unstyled columns.
  useLayoutEffect(() => {
    if (!selectedColumns) return;
    if (!colWidthStyleRef.current) {
      const style = document.createElement('style');
      style.setAttribute('data-col-persist', tableIdRef.current);
      document.head.appendChild(style);
      colWidthStyleRef.current = style;
    }
    const id = tableIdRef.current;
    const base = `[data-pt="${id}"] > .p-datatable > .p-datatable-wrapper > .p-datatable-table`;
    const offset = numberOfChildren;
    let css = '';
    columnsToDisplay.forEach((col, index) => {
      const sCol = selectedColumns.find(s => s.field === col.field);
      const origCol = columns.find(c => c.field === col.field);
      const w = parseInt(sCol?.width, 10) || parseInt(origCol?.style?.minWidth, 10);
      if (!w || isNaN(w)) return;
      const nth = index + offset + 1;
      css += `${base} > .p-datatable-thead > tr > th:nth-child(${nth}),
              ${base} > .p-datatable-tbody > tr > td:nth-child(${nth}),
              ${base} > .p-datatable-tfoot > tr > td:nth-child(${nth}) {
                width: ${w}px !important; min-width: ${w}px !important; max-width: ${w}px !important;
              }\n`;
    });
    colWidthStyleRef.current.innerHTML = css;
    // Keep at end of <head> so it takes priority over PrimeReact's resize styles
    document.head.appendChild(colWidthStyleRef.current);
    return () => {
      if (colWidthStyleRef.current?.parentNode) {
        colWidthStyleRef.current.parentNode.removeChild(colWidthStyleRef.current);
        colWidthStyleRef.current = null;
      }
    };
  }, [selectedColumns, columnsToDisplay, numberOfChildren]);

  const handleFiltersChange = ({ key, value, active = false, like, splitKey=true }) =>{
    let keys
    if(splitKey){
      keys = key.split(".")
    }else {
      keys=[key]
    }
    const newValue =parseFilterValue? (like && value!=null ? `<L>${value}<L>`:value) :value
    let newFilters=filters
    switch(keys.length){
      case 1:newFilters = {
        ...filters,
        params: {
          ...filters.params,
          [keys[0]]: newValue,
        }
      }
        break
      case 2: newFilters = {
        ...filters,
        params: {
          ...filters.params,
          [keys[0]]: {
            ...filters.params[keys[0]],
            [keys[1]]: newValue
          },
        }
      }
        break
      default: return
    }
    const newTableRows = {
      ...tableRows
    }
    setFilters(newFilters);
    if (tableRows.pageSize != 1) {
      setTableRows((tableRows) => ({ ...tableRows, currentPage: 1 }))
      newTableRows.currentPage = 1
    }

    if (onRemoteDataChange && !mobileFilterOpenRef.current && (active || value==null || typeof value=="string" || typeof value==="number")) onRemoteDataChange({ filters: newFilters, tableRows:newTableRows })
  }
  const handleFilterDebounce = useCallback(
    debounce(
      async ({ e, selectedKey, selectedValue, multiSelect, like, splitKey=true }) => {
        const key = e ? e.target.name : selectedKey;
        const value = e ? e.target.value : selectedValue;
        handleFiltersChange({ key, value, multiSelect, like, splitKey })
      },
      [250]
    ),
    [filters,columnDeps]
  );

  const triStateFilter = useCallback(
    ({ filterOptions,...props }) => {
      const field = props.field  ?? filterOptions?.field
      let value = filters?.params?.[field]


      if(value){
        value = props?.type === "BOOL"?value : value == "true"
      }
      const simpleValue = simpleTable ? (filters?.[field]?.value ?? null) : value;
      return (
        <div className="d-flex justify-content-center">
        <TriStateCheckbox
          value={simpleTable ? simpleValue : value}
          onChange={(e) => {
            if(simpleTable){
              setFilters(prev => ({
                ...prev,
                [field]: { ...prev[field], value: e.value ?? null }
              }));
            }else{
              let value = props?.type === "BOOL" ? e.value : (e.value ? "true" : "false")
              value=props?.value ?? value
              handleFiltersChange({key:field, value:e.value == null ? null : value,active:true})
            }

          }}
        />
        </div>

      );
    },
    [filters, simpleTable]
  );
  
  const verifiedBodyTemplate = useCallback((rowData, field) => {
    return (
      <div className="d-flex justify-content-center align-items-center">
      <i
        className={classNames("pi", {
          "true-icon pi-check-circle success": rowData?.[field],
          "false-icon pi-times-circle danger": !rowData?.[field],
        })}
      ></i>
      </div>

    );
  }, []);

  const dropdownFilter = useCallback(
    ({ filterOptions, filterKey, showClear = true,options,showReset=true,splitKey,resetLabel, ...props }) => {
      const field = filterKey ?? filterOptions?.field
      let value = simpleTable? filterOptions.value:filters?.params?.[field]

      const dropdownOptions= showReset ?[{
        [props?.optionValue ?? "value"]:null,
        [props?.optionLabel ?? "label"]: resetLabel ??t("vat.none")
    },].concat(options) :options

      return (
        <Dropdown
          value={value}
          style={{ width: "100%", height: "35px", fontSize: "14px" }}
          onChange={(e) => {
            simpleTable ? filterOptions.filterApplyCallback(e.value) : handleFiltersChange({key:e?.value?.key ??  field ,value:e.value,splitKey})
          }}
          options={dropdownOptions}
          {...props}
        />
      );
    },
    [filters]
  );

  const itemTemplate = (option) => {
    return <div className="w-full d-flex justify-content-between align-items-center">
      <div>
        {option.label}
      </div>
      <div className="badge bg-primary rounded-pill" style={{marginLeft:10}}>
        {option?.count ?? 0}
      </div>
    </div>
  }

  const multiSelectFilter = useCallback(
    ({ filterOptions, showClear = true, showCount = false, dataKey = "value", optionLabel = "label",...props }) => {
      const value = filters?.params?.[filterOptions.field]



      return (
        <MultiSelect
          filter={true}
          options={filterOptions}
          value={simpleTable?filterOptions.value :value}
          dataKey={dataKey}
          optionLabel={optionLabel}
          onChange={(e)=>{
            const data= e.value
            simpleTable ? filterOptions.filterApplyCallback(data) : handleFiltersChange({key:filterOptions.field, value:data,active:true})
          }}
          {...showCount && { itemTemplate}}
          {...props}
          selectAll={false}
        />

      );
    },
    [filters]
  );

  const dateFilterTemplate = useCallback(
    ({ filterOptions, format = 'DD/MM/YYYY', placeholder = 'dd/mm/yyyy',minMaxDate=true, ...props }) => {
      const field=props.field ?? filterOptions.field
      let value = filters?.params?.[field]
      const parsedSelected = value ? moment(value, format).toDate():null
      const minMaxDateProps = minMaxDate ? {
        minDate: filters.fromDate,
        maxDate: filters.toDate,
        footerTemplate: () => (<div className="d-flex alert align-items-center alert-warning" role="alert" style={{ maxWidth: "100%" }}>
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          <div style={{ fontSize: "14px" }}>
            {t("common.calendarAlert")}
          </div>
        </div>)
      } : {}


      return (
        <Calendar
          style={{ maxWidth: 200 }}
          onInput={(e) => {
            const val = e?.target?.value;
            const check = val === '' || val === undefined;
            if (check){
              simpleTable?filterOptions.filterApplyCallback(e.target.value):handleFiltersChange({key:field, value:e.value ? moment(e.value).format(format) : null})
            } 
          }}
          onClearButtonClick={(e) => { simpleTable? filterOptions.filterApplyCallback("") :handleFiltersChange({key:field, value:null })}}
          onChange={(e) => {
            if (moment(e.value).format(format) !== 'Invalid date'){
              simpleTable ? filterOptions.filterApplyCallback(e?.value ??"") : handleFilterDebounce({selectedKey:field,selectedValue: e.value ? moment(e.value).format(format) : ""})
            }
          }}
          showButtonBar
          dateFormat={"dd/mm/yy"}
          placeholder={placeholder}
          value={parsedSelected}
          {...props}
          {...minMaxDateProps}
        />
      );
    },
    [filters]
  );

  const buildFilterElement = useCallback(
    (column) => {

      if (column?.filter == false) return {};
      if (column?.filterType === "DROPDOWN") {
        return {
          filterElement: (filterOptions) =>
            dropdownFilter({
              filterOptions,
              placeholder: column?.filterPlaceholder ?? "",
              ...column.filterConfig,
            }),
        };

      } else if (column?.filterType === "MULTISELECT") {
        return {
          filterElement: (filterOptions) => multiSelectFilter({
            filterOptions,
            placeholder: column?.filterPlaceholder ?? "",
            ...column.filterConfig,
          })
        }
      }

      else if (column?.filterType === "TRISTATECHECKBOX") {
        return {
          filterElement: (filterOptions) => triStateFilter({ filterOptions, ...column.filterConfig }),
        };
      } else if (column.filterType === 'DATE') {
        return {
          filterElement: (filterOptions) =>
            dateFilterTemplate({
              filterOptions,
              filterType: column.filterType,
              ...column.filterConfig,
            }),
        };
      } else {
        return {
          filterElement: (filterOptions) =>(
          <InputFilter 
            filterRefreshed={filterRefreshed}
            handleFiltersChange={handleFiltersChange} 
            simpleTable={simpleTable} 
            like={likeValues} 
            filters={filters}  
            filterOptions={filterOptions} 
            placeholder={column?.filterPlaceholder} 
            columnDeps={columnDeps}
            defaultFilters={defaultFilters}
            {...column.filterConfig}
          />)
        };
      }
    },
    [filters,columnDeps]
  );

  const mappedTableColumns = useMemo(() => {
    return tableColumns.map((column) => {
      return {
        filter: false,
        showFilterMenu: false,
        showClearButton: false,
        ...buildFilterElement(column),
        ...(column?.dataType === "boolean"
        ? { body: (row) => verifiedBodyTemplate(row, column?.bodyConfig?.field ?? column.field) }
        : {}),
        ...column,
      };
    });
  }, [tableColumns]);

  const refreshTableFilters = () => {
    //workaround to refresh filters after column selection since prime react throws an error inside the library
    setFilterRefresh(true)
    setTimeout(() => {
      setFilterRefresh(false)
    }, 200)
  }

  const handleGlobalFilter = useCallback(
    debounce(
      async (value) => {
        if(onRemoteDataChange){
          onRemoteDataChange({searchQuery:value})
        }
      },
      [250]
    ),
    []
  );

  const filterCount=useMemo(()=>{
    let count = 0
    if(simpleTable){
      if(filters){
        Object.keys(filters).forEach(key=>{
          const value = filters[key]?.value
          if(value != null && value !== ""){
            const isArray = Array.isArray(value)
            isArray ? count = count + value.length : count++
          }
        })
      }
    }else{
      const params=filters?.params
      if(defaultFilters && params){
        Object.keys(params).forEach(key=>{
          const value=filters?.params[key]

          const isDefault=JSON.stringify(value) === JSON.stringify(defaultFilters?.params?.[key])
          if(!isDefault && value !=null){
            const isArray= Array.isArray(value)
            isArray?count=count+value.length:count++
          }
        })
      }
    }
    return count
  },[defaultFilters,filters,simpleTable])

  const openColumnPanel = useCallback(() => {
    if (columnPanelRef.current) {
      const toggle = columnPanelRef.current.querySelector('.my-dropdown-toggle');
      if (toggle) toggle.click();
    }
  }, []);

  const renderHeader = useCallback(() => {
    return (
      <div className="prime-table-toolbar">
        <div className="prime-table-toolbar__left">
          {headerButtonsLeft}
        </div>
        <div className="prime-table-toolbar__actions">
          {headerButtonsRight && (
            <div className="prime-table-toolbar__primary-actions">
              {headerButtonsRight}
            </div>
          )}

          {headerIcons}

          {clearFilters && (
            <TooltipOverlay title={t("queue.clearFilters")} placement="bottom">
              <div
                className={`prime-table-toolbar__icon-btn ${filterCount > 0 ? 'prime-table-toolbar__icon-btn--active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (onClearFilter) onClearFilter();
                  clearFilter();
                }}
              >
                <FontAwesomeIcon icon={faFilterCircleXmark} />
                {filterCount > 0 && (
                  <span className="prime-table-toolbar__badge">{filterCount}</span>
                )}
              </div>
            </TooltipOverlay>
          )}

          {showGlobalSearch && (
            <div className="prime-table-toolbar__global-search">
              <i className="pi pi-search search-icon" />
              <input
                value={globalFilterValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setGlobalFilterValue(value);
                  if (searchableKeys?.length == 0) handleGlobalFilter(value);
                }}
                placeholder="Search..."
              />
              {globalFilterValue?.length > 0 && (
                <div className="close-container" onClick={() => { setGlobalFilterValue(""); if (searchableKeys?.length == 0) { handleGlobalFilter("") } }}>
                  <FontAwesomeIcon icon={faTimes} />
                </div>
              )}
            </div>
          )}


          {showSelectColumns && (
            <div className="prime-table-toolbar__column-panel" ref={columnPanelRef}>
              <TooltipOverlay title={t("table.addRemoveCol")} placement="left">
                <div
                  className="prime-table-toolbar__icon-btn"
                  onClick={openColumnPanel}
                >
                  <FontAwesomeIcon icon={faTableColumns} />
                </div>
              </TooltipOverlay>
              <MyMultiSelect
                columns={columns}
                newColumns={newColumns}
                currentColumns={currentColumns}
                selectedColumns={currentColumns}
                setSelectedColumns={setSelectedColumnsWithSave}
                onChange={() => { refreshTableFilters() }}
              />
            </div>
          )}

        </div>
      </div>
    );
  });

  const header = renderHeader();
  const showHeaderCheck = ((headerButtonsRight || headerButtonsLeft || headerIcons || selectedColumns || showGlobalSearch) && showHeader)

  const handlePagination = (e) => {
    setTableRows((tr) => ({
      ...tr,
      rowsPerPage: e.value,
      currentPage: 1
    }));
    if(setPersistedRowPerPage){
      setPersistedRowPerPage(e.value)
    }
    if (onRemoteDataChange && !simpleTable) {
      onRemoteDataChange({
        tableRows: {
          ...tableRows,
          rowsPerPage: e.value,
          currentPage: 1
        }
      })
    }

  };

  const handlePageChange = (option) => {
    let currentPage = tableRows.currentPage
    if (option === "next") {
      if (tableRows.rowsPerPage * tableRows.currentPage < totalRows) {
        currentPage = tableRows.currentPage + 1
      }
    } else if (option === "prev") {
      if (tableRows.rowsPerPage * (tableRows.currentPage - 1) >= 0) {
        currentPage = tableRows.currentPage - 1
      }
    } else if (option === "last") {
      if (tableRows.rowsPerPage * tableRows.currentPage < totalRows) {
        currentPage = Math.ceil(totalRows / tableRows.rowsPerPage)
      }
    } else if (option === "first") {
      if (tableRows.currentPage > 1) {
        currentPage = 1
      }
    }

    setTableRows((tr) => {
      return {
        ...tr,
        currentPage,
      }
    })

    if (onRemoteDataChange && !simpleTable) { onRemoteDataChange({ tableRows: { ...tableRows, currentPage } }) }
  };

  const template2 = {
    layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink",
    RowsPerPageDropdown: (options) => {
      const dropdownOptions = [
        { label: 10, value: 10 },
        { label: 15, value: 15 },
        { label: 20, value: 20 },
        { label: 50, value: 50 },
        { label: 100, value: 100 },
        { label: 200, value: 200 },
      ];

      return (
        <React.Fragment>
          <span
            className="mx-1"
            style={{ color: "var(--text-color)", userSelect: "none" }}
          >
            {t("paginator.itemsPage")}:{" "}
          </span>
          <Dropdown
            value={tableRows.rowsPerPage}
            options={dropdownOptions}
            onChange={handlePagination}
            style={{ height: "35px", fontSize: "14px" }}
          />
        </React.Fragment>
      );
    },
    CurrentPageReport: (options) => {
      const first=simpleTable ? options.first:(totalRows === 0? 0: tableRows.rowsPerPage * (tableRows.currentPage - 1) + 1)
      const last = simpleTable ? options.last:(tableRows.rowsPerPage * tableRows.currentPage > totalRows? totalRows: tableRows.rowsPerPage * tableRows.currentPage)
      const rows=simpleTable?options.totalRecords:totalRows
      return (
        <span
          style={{
            color: "var(--text-color)",
            userSelect: "none",
            width: "120px",
            textAlign: "center",
          }}
        >
          {first}{" "}
          -{" "}
          {last}{" "}
          {t("paginator.of")}{" "}{rows}
        </span>
      );
    },
    FirstPageLink: (options) => {
      let disabled = false;
      if(simpleTable){
        disabled=options.disabled
      }else{
        if (tableRows.currentPage === 1) {
          disabled = true;
        } else {
          disabled = false;
        }
      }

      return (
        <div
          type="button"
          className={`p-paginator-prev p-paginator-element p-link ${disabled ? `p-disabled` : ""
            }`}
          onClick={() => handlePageChange("first")}
          disabled={false}
        >
          {options.element}
        </div>
      );
    },
    PrevPageLink: (options) => {
      let disabled = false;
      if(simpleTable){
        disabled=options.disabled
      }else{
        if (tableRows.rowsPerPage * (tableRows.currentPage - 1) + 1 === 1) {
          disabled = true;
        } else {
          disabled = false;
        }
      }

      return (
        <div
          type="button"
          className={`p-paginator-prev p-paginator-element p-link ${disabled ? `p-disabled` : ""
            }`}
          onClick={() => handlePageChange("prev")}
          disabled={false}
        >
          {options.element}
        </div>
      );
    },
    NextPageLink: (options) => {
      let disabled = false;
      if(simpleTable){
        disabled=options.disabled
      }else{
        if (tableRows.rowsPerPage * tableRows.currentPage < totalRows) {
          disabled = false;
        } else {
          disabled = true;
        }
      }


      return (
        <div
          type="button"
          className={`p-paginator-prev p-paginator-element p-link ${disabled ? `p-disabled` : ""
            }`}
          onClick={() => handlePageChange("next")}
          disabled={disabled}
        >
          {options.element}
        </div>
      );
    },
    LastPageLink: (options) => {
      let disabled = false;
      if(simpleTable){
        disabled=options.disabled
      }else{
        if (tableRows.rowsPerPage * tableRows.currentPage < totalRows) {
          disabled = false;
        } else {
          disabled = true;
        }
      }

      return (
        <div
          type="button"
          className={`p-paginator-prev p-paginator-element p-link ${disabled ? `p-disabled` : ""
            }`}
          onClick={() => handlePageChange("last")}
          disabled={disabled}
        >
          {options.element}
        </div>
      );
    },
  };

  function adjustIndex(dropIndex, array) {
    let adjustedIndex = 0;
    let oldDrop=dropIndex

    for (let i = 0; i < array.length; i++) {
      if ((array[i].show==false || array[i].show==undefined) && array[i]?.persisted!=true) {
        oldDrop++;
      }
      else if (adjustedIndex === oldDrop) {
        return i;
      }
      adjustedIndex++;
    }
  }


  const booleanFields = useMemo(() => {
    return columns
      .filter(col => col.dataType === "boolean" || col.filterType === "TRISTATECHECKBOX")
      .map(col => col.field);
  }, [columns]);

  const normalizedValue = useMemo(() => {
    if (!simpleTable || booleanFields.length === 0 || !Array.isArray(value)) return value;
    return value.map(row => {
      const normalized = { ...row };
      booleanFields.forEach(field => {
        const raw = getNestedValue(row, field);
        if (raw !== null && raw !== undefined) {
          normalized[field] = Boolean(raw) && raw !== "false" && raw !== "0" && raw !== 0;
        }
      });
      return normalized;
    });
  }, [value, simpleTable, booleanFields]);

  const globalFilteredData=useMemo(( )=>{
    const source = simpleTable ? normalizedValue : value;
    if(searchableKeys?.length>0){
      if (globalFilterValue?.length == 0) return source
      return source.filter((val) => {
        const check = searchableKeys.some(key => getNestedValue(val,key)?.toString()?.toLowerCase().includes(globalFilterValue.toLowerCase()))
        return check
      }
      )
    }
    return source
  }, [value, normalizedValue, simpleTable, globalFilterValue,showGlobalSearch])

  // For simpleTable, apply client-side column filters manually for both desktop and mobile.
  // PrimeReact's internal EQUALS filter mishandles boolean false (treats falsy as no filter),
  // so we filter ourselves and pass pre-filtered rows to the DataTable.
  const mobileFilteredData = useMemo(() => {
    if (!simpleTable || !filters) return globalFilteredData;
    return globalFilteredData.filter((row) => {
      return Object.entries(filters).every(([field, filterMeta]) => {
        if (filterMeta?.value === null || filterMeta?.value === undefined || filterMeta?.value === "") return true;
        const cellValue = getNestedValue(row, field);
        const filterValue = filterMeta.value;
        switch (filterMeta.matchMode) {
          case FilterMatchMode.CONTAINS:
            return cellValue?.toString()?.toLowerCase()?.includes(filterValue.toString().toLowerCase());
          case FilterMatchMode.EQUALS:
            if (typeof filterValue === "boolean") {
              return Boolean(cellValue) === filterValue;
            }
            return cellValue === filterValue || cellValue?.toString() === filterValue?.toString();
          case FilterMatchMode.IN:
            return Array.isArray(filterValue) ? filterValue.includes(cellValue) : true;
          default:
            return true;
        }
      });
    });
  }, [simpleTable, filters, globalFilteredData]);

  const handleColumnReorder = (e) => {
    let { dragIndex, dropIndex } = e;
    dragIndex = dragIndex - numberOfChildren//number of children is needed for in order so the grid doesn't calculate unwanted columns such as select all for the reorder
    dropIndex = dropIndex - numberOfChildren

    const fullColumns = [...newColumns, ...currentColumns];

    const draggedColumn = tableColumns[dragIndex];
    const actualDragIndex = fullColumns.findIndex(col => col.field === draggedColumn.field);
    let actualDropIndex = adjustIndex(dropIndex,fullColumns)

    const element = fullColumns.splice(actualDragIndex, 1)[0];
    fullColumns.splice(actualDropIndex, 0, element);
    const mappedItems=fullColumns.map(item=>{
      const existing = selectedColumns?.find(col => col.field === item.field);
      return {field:item.field,show:item.show,...(item.persisted?{persisted:true}:{}),...(existing?.width?{width:existing.width}:{})}
    })

    setSelectedColumnsWithSave(mappedItems);

  };

  const handleMobileGlobalFilterChange = useCallback((value) => {
    setGlobalFilterValue(value);
    if (searchableKeys?.length === 0 && handleGlobalFilter) {
      handleGlobalFilter(value);
    }
  }, [searchableKeys, handleGlobalFilter]);

  const handleMobileGlobalFilterClear = useCallback(() => {
    setGlobalFilterValue("");
    if (searchableKeys?.length === 0 && handleGlobalFilter) {
      handleGlobalFilter("");
    }
  }, [searchableKeys, handleGlobalFilter]);

  return (
    <>
    <div ref={wrapperRef} data-pt={tableIdRef.current} className={`datatable-rowgroup-demo ${!noCard ? 'prime-table-card' : ''} ${compact ? "compact" : ""} ${isMobile && showHeader ? "prime-table-mobile-active" : ""}`}>
    <DataTable
      {...(filterRefresh ? { filters:[] } : {})}
      {...(simpleTable ? {
        filters: Object.fromEntries(
          Object.entries(filters || {}).map(([field, meta]) => [
            field,
            // Strip boolean filter values so PrimeReact doesn't re-filter; we already filtered client-side
            typeof meta?.value === "boolean" ? { ...meta, value: null } : meta
          ])
        ),
        onFilter: (e) => setFilters(prev => ({
          ...e.filters,
          // Preserve our boolean filter values that we stripped above
          ...Object.fromEntries(
            Object.entries(prev || {}).filter(([, meta]) => typeof meta?.value === "boolean")
          )
        }))
      } : {})}
      header={showHeaderCheck ?header:null}
      responsiveLayout="scroll"
      size="sm"
      filterDisplay={showFilters ? filterDisplay : false}
      className="white-header"
      paginator={paginator}
      paginatorTemplate={template2}
      paginatorClassName="justify-content-end"
      rows={tableRows.rowsPerPage}
      sortMode="single"
      stripedRows={stripedRows}
      sortField={tableRows?.sortCriteria?.field}
      sortOrder={tableRows?.sortCriteria?.direction ? (tableRows?.sortCriteria?.direction === 'ASC' ? 1 : -1):0}
      value={simpleTable ? mobileFilteredData : globalFilteredData}
      onColReorder={(props)=>{if(setSelectedColumns) handleColumnReorder(props)}}
      scrollable={true}
      resizableColumns
      columnResizeMode="expand"
      onColumnResizeEnd={handleColumnResizeEnd}
      onSort={onSort}
      // {...(setSelectedColumns ? { onColReorder } : {})}
      {...props}
    >
      {children}
      {mappedTableColumns.map((column) => (
        <Column  key={column.field} {...column} />
      ))}
      {props.reorderableRows && <Column rowReorder={rowReorder} />}
    </DataTable>
    {isMobile && showHeader && paginator && (
      <MobileCardList
        value={mobileFilteredData}
        columns={columns}
        dataKey={dataKey}
        tableRows={tableRows}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onSort={onSort}
        filters={filters}
        setFilters={setFilters}
        buildFilterElement={buildFilterElement}
        clearFilter={clearFilter}
        filterCount={filterCount}
        expandedRows={props.expandedRows}
        onRowExpand={props.onRowExpand ?? (props.onRowToggle ? (e) => props.onRowToggle({ data: [e.data] }) : undefined)}
        onRowCollapse={props.onRowCollapse ?? (props.onRowToggle ? () => props.onRowToggle({ data: [] }) : undefined)}
        rowExpansionTemplate={props.rowExpansionTemplate}
        mobileExpansionTemplate={props.mobileExpansionTemplate}
        mobileFilterContent={props.mobileFilterContent}
        mobileExpansionLabel={props.mobileExpansionLabel}
        selectionBody={props.mobileSelectionBody}
        showGlobalSearch={showGlobalSearch}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={handleMobileGlobalFilterChange}
        onGlobalFilterClear={handleMobileGlobalFilterClear}
        paginator={paginator}
        headerButtonsLeft={headerButtonsLeft}
        headerButtonsRight={headerButtonsRight}
        headerIcons={headerIcons}
        simpleTable={simpleTable}
        onFilterSheetOpen={() => { mobileFilterOpenRef.current = true; }}
        onFilterSheetApply={({ skipRemote } = {}) => {
          mobileFilterOpenRef.current = false;
          if (!skipRemote && onRemoteDataChange && !simpleTable) {
            onRemoteDataChange({ filters, tableRows: { ...tableRows, currentPage: 1 } });
          }
        }}
      />
    )}
    </div>
    </>

  );
};

export const onColReorder = ({ reorder, setSelectedColumns ,childrenCount}) => {

  if (!reorder) {
    console.error('onColReorder called without reorder argument');
    return;
}
  
    const { dragIndex, dropIndex } = reorder
    setSelectedColumns((selectedColumns) => {
      let _selectedCols = [...selectedColumns];
      const temp = _selectedCols[dragIndex - childrenCount];
      _selectedCols.splice(dragIndex - childrenCount, 1);
      _selectedCols.splice(dropIndex - childrenCount, 0, temp);
      return _selectedCols;
    });
  }
  
export const verifiedBodyTemplate = (check) => {
  return (
    <div className="d-flex justify-content-center">
    <i
      className={classNames("pi", {
        "true-icon pi-check-circle success": check,
        "false-icon pi-times-circle danger": !check,
      })}
    ></i>
    </div>

  );
}

const InputFilter = ({handleFiltersChange,filters,simpleTable, filterOptions, showClear = true, multiselect=false,like=true,splitKey=true,filterRefreshed,columnDeps,disabled,...props }) => {
    const field=props.field ?? filterOptions.field
    const [value,setValue]=useState(filters?.params?.[field] ?? "")
    const { debounce } = func;

    const handleFilterDebounce = useCallback(
      debounce(
        async ({ e, selectedKey, selectedValue, multiSelect, like, splitKey=true }) => {
          const key = e ? e.target.name : selectedKey;
          const value = e ? e.target.value : selectedValue;
          handleFiltersChange({ key, value, multiSelect, like, splitKey })
        },
        [450]
      ),
      [filters,columnDeps]
    );

    useEffect(() => {
      if(filterRefreshed!=null){
        if(simpleTable){
          setValue("");
        }else{
          const defaultValue = filters?.params?.[field]
          setValue(defaultValue ?? "");
        }
      }
    }, [filterRefreshed]);

    useEffect(() => {
      if(simpleTable && filterOptions?.value === null){
        setValue("");
      }
    }, [filterOptions?.value]);
  

    return (
      <input
        className="p-inputtext p-component p-column-filter input-filter-clearable"
        onChange={(e) => {
          const value = e.target.value?.length == 0 ? null : e.target.value
          setValue(value)
          if(simpleTable){
            filterOptions.filterApplyCallback(value);
          }else{
            handleFilterDebounce({ selectedKey: field, selectedValue: value,multiselect,like,splitKey});
          }
        }}
        value={value}
        disabled={disabled}
      />
    );
  }


export const parseFilterParams=({filters,columns,likeValues=true})=>{
  const newParams={}
  const params=filters.params

  Object.keys(params).forEach(key=>{
    const column=columns.find(col=>(col.filterConfig?.field ?? col?.field) === key)
    const isLike=column?.filterConfig?.like!=null ?column.filterConfig.like: likeValues

    if(column && isLike){
      let value=params[key]
      if(isLike && value!=null && value!="" && !Array.isArray(value)){
        value = `<L>${value}<L>`
        Object.assign(newParams,{[key]:value})
      }
    }
  })
  
  return {...filters,params:{...filters.params,...newParams}}


}



export default PrimeTable;
